import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import ShareBox from '../../components/ShareBox';
import { 
  TrendingUp, TrendingDown, ArrowRight, ArrowLeft,
  ChevronRight, Zap, AlertTriangle, CheckCircle, Clock,
  BarChart3, Activity, Target, Brain, Shield, Layers
} from 'lucide-react';

// FAQ Data
const faqData = [
  {
    question: "Destek seviyesi nedir?",
    answer: "Destek seviyesi, fiyatın düşüşünün durma eğilimi gösterdiği veya tepki verdiği fiyat bölgesidir. Alıcıların yoğunlaştığı ve satış baskısının azaldığı seviye olarak düşünülebilir."
  },
  {
    question: "Direnç seviyesi nedir?",
    answer: "Direnç seviyesi, fiyatın yükselişinin durma eğilimi gösterdiği veya geri döndüğü fiyat bölgesidir. Satıcıların yoğunlaştığı ve alım baskısının azaldığı seviye olarak düşünülebilir."
  },
  {
    question: "Destek kırılınca ne olur?",
    answer: "Destek kırıldığında, o seviye direnç haline dönüşebilir. Buna 'rol değişimi' (role reversal) denir. Kırılma genellikle düşüş trendinin devamına işaret eder."
  },
  {
    question: "Destek ve direnç nasıl belirlenir?",
    answer: "1) Geçmiş fiyat tepkilerine bakın (çoklu dokunuş), 2) Yuvarlak sayılar (50.000, 100.000), 3) Hareketli ortalamalar, 4) Fibonacci seviyeleri, 5) Hacim profili yoğunluk bölgeleri."
  },
  {
    question: "Support ve resistance her zaman çalışır mı?",
    answer: "Hayır. Destek ve direnç seviyeleri kesin sınırlar değil, olasılık bölgeleridir. Güçlü haberlerde veya yüksek volatilitede kolayca kırılabilirler. Her zaman stop-loss kullanın."
  }
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Destek ve Direnç Nedir? Support ve Resistance Kullanımı",
    "description": "Destek ve direnç seviyeleri nedir, nasıl belirlenir ve nasıl kullanılır? Kripto trading'de support ve resistance rehberi.",
    "author": { "@type": "Organization", "name": "Borsamex" },
    "publisher": { "@type": "Organization", "name": "Borsamex", "logo": { "@type": "ImageObject", "url": "https://borsamex.com/logo512.png" } },
    "datePublished": "2026-04-02",
    "dateModified": "2026-04-02",
    "mainEntityOfPage": "https://borsamex.com/rehber/destek-direnc-nedir"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({ "@type": "Question", "name": item.question, "acceptedAnswer": { "@type": "Answer", "text": item.answer } }))
  }
];

const DestekDirencNedirPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="Destek ve Direnç Nedir? Support ve Resistance Kullanımı | Borsamex"
        description="Destek ve direnç seviyeleri nedir, nasıl belirlenir ve nasıl kullanılır? Kripto trading'de support, resistance, rol değişimi ve kırılma stratejileri rehberi."
        keywords="destek ve direnç nedir, support resistance, destek seviyesi, direnç seviyesi, destek kırılması, direnç kırılması, kripto teknik analiz"
        canonical="https://borsamex.com/rehber/destek-direnc-nedir"
        structuredData={structuredData}
      />

      {/* Breadcrumb */}
      <div className="border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-white">Ana Sayfa</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/rehber" className="hover:text-white">Rehber</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">Destek ve Direnç</span>
          </div>
        </div>
      </div>

      <article className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          
          {/* Header */}
          <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">Teknik Analiz</Badge>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="h-4 w-4" />
                <span>7 dk okuma</span>
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              Destek ve Direnç Nedir? (Support & Resistance)
            </h1>
            
            <div className="p-6 bg-slate-900/50 border border-slate-700/50 rounded-xl">
              <p className="text-lg text-slate-300 leading-relaxed">
                <strong className="text-white">Destek ve direnç</strong>, fiyatın tepki verme eğilimi 
                gösterdiği kritik seviyelerdir. 
                <strong className="text-green-400"> Destek</strong> fiyatın düşüşünün durduğu, 
                <strong className="text-red-400"> direnç</strong> ise yükselişinin durduğu bölgedir. 
                Teknik analizin temel taşlarıdır.
              </p>
            </div>
          </motion.header>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="prose prose-invert prose-slate max-w-none">
            
            {/* Section 1 - What is Support */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-green-400" />
                </div>
                Destek Seviyesi (Support) Nedir?
              </h2>
              
              <p className="text-slate-300 mb-4">
                <strong className="text-green-400">Destek seviyesi</strong>, fiyatın düşerken duraksadığı 
                veya geri döndüğü bölgedir. Bu seviyede alıcılar devreye girer ve satış baskısı azalır.
              </p>
              
              <Card className="border-green-500/30 bg-green-950/20 mb-6">
                <CardContent className="p-6">
                  <h3 className="text-green-400 font-bold mb-3">Destek Seviyesi Özellikleri</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-3 text-slate-300">
                      <CheckCircle className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
                      <span>Fiyat bu seviyeye geldiğinde <strong>alıcılar</strong> aktifleşir</span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-300">
                      <CheckCircle className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
                      <span>Ne kadar çok test edilirse o kadar <strong>güçlü</strong> kabul edilir</span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-300">
                      <CheckCircle className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
                      <span>Kırıldığında <strong>direnç</strong> haline dönüşebilir</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </section>

            {/* Section 2 - What is Resistance */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Layers className="h-4 w-4 text-red-400" />
                </div>
                Direnç Seviyesi (Resistance) Nedir?
              </h2>
              
              <p className="text-slate-300 mb-4">
                <strong className="text-red-400">Direnç seviyesi</strong>, fiyatın yükselirken duraksadığı 
                veya geri döndüğü bölgedir. Bu seviyede satıcılar devreye girer ve alım baskısı azalır.
              </p>
              
              <Card className="border-red-500/30 bg-red-950/20 mb-6">
                <CardContent className="p-6">
                  <h3 className="text-red-400 font-bold mb-3">Direnç Seviyesi Özellikleri</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-3 text-slate-300">
                      <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                      <span>Fiyat bu seviyeye geldiğinde <strong>satıcılar</strong> aktifleşir</span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-300">
                      <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                      <span>Geçmiş zirveler genellikle <strong>direnç</strong> oluşturur</span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-300">
                      <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                      <span>Kırıldığında <strong>destek</strong> haline dönüşebilir</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </section>

            {/* Section 3 - How to Identify */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Target className="h-4 w-4 text-purple-400" />
                </div>
                Destek ve Direnç Nasıl Belirlenir?
              </h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-brand-green font-bold shrink-0">1</div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Geçmiş Fiyat Tepkileri</h3>
                    <p className="text-slate-400 text-sm">
                      Fiyatın geçmişte birden fazla kez tepki verdiği seviyeler önemlidir. 
                      Ne kadar çok dokunuş, o kadar güçlü seviye.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-brand-green font-bold shrink-0">2</div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Yuvarlak Sayılar (Psikolojik Seviyeler)</h3>
                    <p className="text-slate-400 text-sm">
                      $50.000, $100.000 gibi yuvarlak rakamlar doğal destek/direnç oluşturur. 
                      İnsanlar bu seviyelerde emir verme eğilimindedir.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-brand-green font-bold shrink-0">3</div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Hareketli Ortalamalar</h3>
                    <p className="text-slate-400 text-sm">
                      50, 100 ve 200 günlük hareketli ortalamalar dinamik destek/direnç seviyeleri oluşturur.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-brand-green font-bold shrink-0">4</div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Fibonacci Seviyeleri</h3>
                    <p className="text-slate-400 text-sm">
                      <Link to="/rehber/fibonacci-nedir" className="text-brand-green hover:underline">Fibonacci geri çekilme seviyeleri</Link> (%38.2, %50, %61.8) 
                      potansiyel destek/direnç bölgeleridir.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Inline Signal Box */}
            <section className="mb-12">
              <Card className="border-brand-green/30 bg-brand-green/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="h-6 w-6 text-brand-green" />
                    <span className="font-bold text-white">AI Sinyal Örneği</span>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">AVAX-USDT</span>
                      <Badge className="bg-green-500/20 text-green-400">LONG</Badge>
                    </div>
                    <p className="text-slate-400 text-sm mb-2">
                      Güçlü destek seviyesi testi ($35.00) + RSI oversold + Hacim artışı
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Activity className="h-3 w-3" />
                      <span>Çoklu destek konfirmasyonu</span>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mb-4">
                    Borsamex, destek/direnç seviyelerini otomatik tespit eder ve diğer göstergelerle birlikte değerlendirir.
                  </p>
                  <Link to="/signals">
                    <Button variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/10 w-full">
                      Tüm Sinyalleri İncele <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </section>

            {/* Section 4 - Role Reversal */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BarChart3 className="h-4 w-4 text-blue-400" />
                </div>
                Rol Değişimi (Role Reversal)
              </h2>
              
              <p className="text-slate-300 mb-4">
                En önemli kavramlardan biri <strong className="text-white">"rol değişimi"</strong>dir. 
                Kırılan destek direnç haline, kırılan direnç destek haline dönüşebilir.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <Card className="border-slate-700 bg-slate-900/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingDown className="h-5 w-5 text-red-400" />
                      <span className="font-bold text-white">Destek Kırılırsa</span>
                    </div>
                    <p className="text-slate-400 text-sm">
                      Kırılan destek seviyesi artık <strong className="text-red-400">direnç</strong> olur. 
                      Fiyat tekrar bu seviyeye geldiğinde reddedilebilir.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-slate-700 bg-slate-900/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-5 w-5 text-green-400" />
                      <span className="font-bold text-white">Direnç Kırılırsa</span>
                    </div>
                    <p className="text-slate-400 text-sm">
                      Kırılan direnç seviyesi artık <strong className="text-green-400">destek</strong> olur. 
                      Fiyat geri çekildiğinde bu seviyeden sıçrayabilir.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* Section 5 - AI Trading */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-green/20 flex items-center justify-center">
                  <Brain className="h-4 w-4 text-brand-green" />
                </div>
                AI Trading'de Destek ve Direnç
              </h2>
              
              <p className="text-slate-300 mb-4">
                Borsamex'in Sniper Engine'i destek ve direnç seviyelerini şu şekilde kullanır:
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span><strong className="text-white">Otomatik seviye tespiti:</strong> Geçmiş fiyat hareketlerinden kritik seviyeler belirlenir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span><strong className="text-white">Confluence analizi:</strong> Destek/direnç + <Link to="/rehber/fibonacci-nedir" className="text-brand-green hover:underline">Fibonacci</Link> + MA çakışımları öncelikli</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span><strong className="text-white">Gösterge konfirmasyonu:</strong> <Link to="/rehber/rsi-nedir" className="text-brand-green hover:underline">RSI</Link>, <Link to="/rehber/macd-nedir" className="text-brand-green hover:underline">MACD</Link> ile birlikte değerlendirme</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span><strong className="text-white">Kırılma analizi:</strong> Hacim ve momentum ile kırılma doğrulama</span>
                </li>
              </ul>
              
              <Card className="border-amber-500/30 bg-amber-950/20">
                <CardContent className="p-4">
                  <p className="text-amber-200 text-sm flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                    Destek ve direnç seviyeleri kesin sınırlar değil, bölgelerdir. Fiyat bu seviyeleri bir miktar aşıp geri dönebilir.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* FAQ */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">Sık Sorulan Sorular</h2>
              <div className="space-y-4">
                {faqData.map((faq, idx) => (
                  <Card key={idx} className="border-slate-700/50 bg-slate-900/30">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-white mb-2 flex items-start gap-2">
                        <span className="text-brand-green">?</span>
                        {faq.question}
                      </h3>
                      <p className="text-slate-400 text-sm pl-5">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Related Articles */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">İlgili Makaleler</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/rehber/fibonacci-nedir">
                  <Card className="border-slate-700/50 bg-slate-900/30 hover:border-brand-green/50 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="text-white font-bold mb-1">Fibonacci Nedir?</h3>
                      <p className="text-slate-400 text-sm">Destek/direnç ile birlikte kullanılan seviyeler</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/rehber/rsi-nedir">
                  <Card className="border-slate-700/50 bg-slate-900/30 hover:border-brand-green/50 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="text-white font-bold mb-1">RSI Nedir?</h3>
                      <p className="text-slate-400 text-sm">Destek/direnç seviyelerinde konfirmasyon için</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/rehber/macd-nedir">
                  <Card className="border-slate-700/50 bg-slate-900/30 hover:border-brand-green/50 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="text-white font-bold mb-1">MACD Nedir?</h3>
                      <p className="text-slate-400 text-sm">Kırılma momentumunu değerlendirme</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/rehber/tp-ve-stop-loss-nedir">
                  <Card className="border-slate-700/50 bg-slate-900/30 hover:border-brand-green/50 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="text-white font-bold mb-1">TP ve Stop Loss Nedir?</h3>
                      <p className="text-slate-400 text-sm">Destek/direnç bazlı hedef belirleme</p>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </section>

            <ShareBox 
              title="Destek ve Direnc Nedir? Kripto Teknik Analiz"
              url="/rehber/destek-direnc-nedir"
              hashtags="#kripto #destekdirenc #teknikanaliz #trading"
            />

          </motion.div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-8 border-t border-slate-800">
            <Link to="/rehber/fibonacci-nedir" className="flex items-center gap-2 text-slate-400 hover:text-white">
              <ArrowLeft className="h-4 w-4" />
              Önceki: Fibonacci Nedir?
            </Link>
            <Link to="/rehber" className="flex items-center gap-2 text-slate-400 hover:text-white">
              Tüm Rehberler
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-blue-900/20 to-brand-green/10 rounded-2xl border border-slate-700/50 text-center">
            <h3 className="text-xl font-bold text-white mb-3">
              Destek ve Direnç Otomatik Tespit
            </h3>
            <p className="text-slate-400 mb-6">
              Borsamex'in AI sistemi kritik seviyeleri otomatik belirler ve sinyal üretir.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                Ücretsiz Dene <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

        </div>
      </article>
    </div>
  );
};

export default DestekDirencNedirPage;
